import { Entity, parseEntityRef } from '@backstage/catalog-model'
import { Progress, ResponseErrorPanel, Table, TableColumn } from '@backstage/core-components'
import { useApi } from '@backstage/core-plugin-api'
import { AwsLambda } from '@internal/backstage-plugin-veho-backstage-common'
import { Chip } from '@material-ui/core'
import { useState } from 'react'
import useAsync from 'react-use/lib/useAsync'

import { awsLambdaApiRef } from '../../api/AwsLambdaApi'
import { LambdaDetailsDrawer } from '../LambdaDetailsDrawer'

const columns: TableColumn<AwsLambda>[] = [
  {
    title: 'Function Name',
    field: 'functionName',
    width: '40%',
    highlight: true,
  },
  {
    title: 'Region',
    field: 'region',
  },
  {
    title: 'Account ID',
    field: 'accountId',
  },
  {
    title: 'Runtime',
    field: 'runtime',
  },
  {
    title: 'State',
    field: 'state',
    render: row => <Chip label={row.state} size="small" color={row.state === 'Active' ? 'primary' : 'default'} />,
  },
]

export interface AwsLambdaTableProps {
  entity: Entity
}

export const AwsLambdaTable = ({ entity }: AwsLambdaTableProps) => {
  const awsLambdaApi = useApi(awsLambdaApiRef)
  const [selectedLambda, setSelectedLambda] = useState<AwsLambda | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const {
    value: lambdas,
    loading,
    error,
  } = useAsync(async () => {
    const entityRef = parseEntityRef(entity.metadata.name!, {
      defaultKind: entity.kind,
      defaultNamespace: entity.metadata.namespace,
    })

    return awsLambdaApi.getLambdasByEntity(entityRef)
  }, [entity, awsLambdaApi])

  const handleViewDetails = (lambda: AwsLambda) => {
    setSelectedLambda(lambda)
    setDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setSelectedLambda(null)
  }

  if (loading) {
    return <Progress />
  }

  if (error) {
    return <ResponseErrorPanel error={error} />
  }

  if (!lambdas || lambdas.length === 0) {
    return <div style={{ textAlign: 'center', padding: '2rem' }}>No Lambda functions found for this entity.</div>
  }

  return (
    <>
      <Table
        title="AWS Lambda Functions"
        options={{ search: true, paging: true, pageSize: 10 }}
        columns={columns}
        data={lambdas}
        onRowClick={(_event, rowData) => {
          if (rowData) {
            handleViewDetails(rowData as AwsLambda)
          }
        }}
      />
      <LambdaDetailsDrawer lambda={selectedLambda} open={drawerOpen} onClose={handleCloseDrawer} />
    </>
  )
}
